import React, { useCallback, useState, type MouseEvent, type KeyboardEvent } from 'react';
import Button from '@atlaskit/button';
import DropdownMenu, {
	type CustomTriggerProps,
	type OnOpenChangeArgs,
} from '@atlaskit/dropdown-menu';
import ChevronDown from '@atlaskit/icon/glyph/chevron-down';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import Lozenge from '@atlaskit/lozenge';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { fontFallback } from '@atlaskit/theme/typography';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { ColumnState } from '../../../../common/types';
import { getIcon } from '../../../../common/utils/fields';
import { ExistingFields } from './existing-fields';
import messages from './messages';
import { NewCustomFields } from './new-custom-fields';
import { useValidationError } from './utils';

export type Props = {
	fieldMap: ColumnState;
};

export const FieldSelector = ({ fieldMap }: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const [isCustomFieldsMenuOpen, setIsCustomFieldsMenuOpen] = useState(false);
	const { formatMessage } = useIntl();
	const { hasValidationError, validationMessage } = useValidationError(fieldMap.columnName);

	const toggleMenuOpenState = useCallback((attrs: OnOpenChangeArgs) => {
		setIsOpen(attrs.isOpen);

		if (!attrs.isOpen) {
			setIsCustomFieldsMenuOpen(false);
		}
	}, []);

	const dropdownTrigger = useCallback(
		({ triggerRef, ...props }: CustomTriggerProps<HTMLElement>) => {
			const isSelected = Boolean(fieldMap.field);
			return (
				<Stack>
					<Box xcss={hasValidationError ? validationErrorBorderStyles : []}>
						<Button
							{...props}
							iconAfter={<ChevronDown size="medium" label="" />}
							ref={triggerRef}
							isDisabled={!fieldMap.isIncluded}
						>
							{isSelected && fieldMap.isIncluded && (
								<Box xcss={buttonSelectedStyles}>
									<Inline alignBlock="center" space="space.150">
										{getIcon({
											fieldType: fieldMap.field?.fieldType,
											isSelected: isOpen,
										})}
										<Box>{fieldMap.field?.fieldName}</Box>
									</Inline>
									{fieldMap.field?.fieldId === null && (
										<Lozenge appearance="new">{formatMessage(messages.newField)}</Lozenge>
									)}
								</Box>
							)}
							{!fieldMap.isIncluded && (
								<Box xcss={buttonPlaceholderStyles}>{formatMessage(messages.doNotImport)}</Box>
							)}
							{!isSelected && fieldMap.isIncluded && (
								<Box xcss={buttonPlaceholderStyles}>
									{formatMessage(messages.fieldSelectorPlaceholder)}
								</Box>
							)}
						</Button>
					</Box>
					{hasValidationError && (
						<Inline xcss={errorMessageStyles}>
							<ErrorIcon size="small" label="error" primaryColor="color.icon.danger" />
							<Box as="span">{validationMessage}</Box>
						</Inline>
					)}
				</Stack>
			);
		},
		[
			fieldMap.field,
			fieldMap.isIncluded,
			formatMessage,
			hasValidationError,
			isOpen,
			validationMessage,
		],
	);

	const onCustomFieldMenuOpen = useCallback((event: MouseEvent | KeyboardEvent) => {
		event.stopPropagation();
		setIsCustomFieldsMenuOpen(true);
	}, []);

	return (
		<Box testId="project-create-importer-upload-flow.ui.mapping-screen.field-mapping.field-selector.wrapper">
			<DropdownMenu
				testId="project-create-importer-upload-flow.ui.mapping-screen.field-mapping.field-selector.dropdown-menu"
				onOpenChange={toggleMenuOpenState}
				shouldFlip
				isOpen={isOpen}
				placement="bottom-end"
				autoFocus={false}
				trigger={dropdownTrigger}
			>
				<Box>
					{isCustomFieldsMenuOpen && (
						<NewCustomFields fieldMap={fieldMap} onBack={() => setIsCustomFieldsMenuOpen(false)} />
					)}
					{!isCustomFieldsMenuOpen && (
						<ExistingFields fieldMap={fieldMap} onCustomFieldMenuOpen={onCustomFieldMenuOpen} />
					)}
				</Box>
			</DropdownMenu>
		</Box>
	);
};

const buttonPlaceholderStyles = xcss({
	textAlign: 'left',
	width: '252px',
	alignItems: 'center',
	color: 'color.text.subtlest',
});

const buttonSelectedStyles = xcss({
	width: '252px',
	display: 'flex',
	alignItems: 'center',
	flexDirection: 'row',
	justifyContent: 'space-between',
	minWidth: '0',
});

const errorMessageStyles = xcss({
	color: 'color.text.danger',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.body.small', fontFallback.body.small),
	lineHeight: '14px',
	fontWeight: '400',
	gap: 'space.050',
	marginBlockStart: 'space.100',
});

const validationErrorBorderStyles = xcss({
	borderWidth: 'border.width.outline',
	borderStyle: 'solid',
	borderColor: 'color.border.danger',
	borderRadius: 'border.radius.050',
});
